import EventTypes from "../../../shared/EventTypes";
import { FIELD_INPUT } from "../../../tools/actions/ActionHelper";
import AbstractField from "./AbstractField";

export default class InputField extends AbstractField {
    isInputField = true;
    dimensionInput = false;
    idValue = null;

    label(text) {
        this.labelText = text;
        return this;
    }

    attribute(attribute) {
        this.attributeValue = attribute;
        if (!this.idValue) {
            this.idValue = attribute;
        }
        return this;
    }

    id(id) {
        this.idValue = id;
        return this;
    }

    dimension() {
        this.dimensionInput = true;
        return this;
    }

    input(func) {
        this.initialized
            ? this.inputElement.input(func)
            : this.inputFunction = func;
        return this;
    }

    populate(element) {
        this.populateFunction(element, this.inputElement);
    }

    make() {
        this.field = this.p.createDiv();
        this.labelDiv = this.p.createDiv(this.labelText)
            .parent(this.field)
            .class('input-label');
        this.parentElement ? this.field.parent(this.parentElement) : null;
        this.applyClasses();
        this.applyStyles();

        let inputWrapper = this.dimensionInput
            ? this.p.createDiv().parent(this.field).style('position', 'relative').style('width', '100%')
            : this.field;
        this.inputElement = this.dimensionInput
            ? this.p.createInput(0, 'float')
            : this.p.createInput('')
        this.inputElement.parent(inputWrapper)
            .class('input-text')
            .id(this.idValue)
            .input(this.isDrawInput ? (this.inputFunction ?? (() => this.makeSticky())) : (() => this.action?.update()));
        if (this.dimensionInput) {
            this.inputElement
                .style('width', '100%')
                .style('box-sizing', 'border-box')
                .style('padding-right', '2rem');
        }
        this.inputElement.elt.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') {
                this.inputElement.elt.blur();
            }
        });
        this.setFocusAndBlurActions();

        if (this.dimensionInput) {
            this.unit = this.p.createSpan(this.loaded ? (this.settings.metric ? 'm' : 'ft') : 'm')
                .parent(inputWrapper)
                .style('position', 'absolute')
                .style('right', '0.5rem')
                .style('top', '50%')
                .style('transform', 'translateY(-50%)')
                .style('pointer-events', 'none')
                .class('input-label');
            this.on(EventTypes.UPDATE_UNIT_SYSTEM, (isMetric) => this.updateUnitSystem(isMetric));
        }
        this.initialized = true;
        return this;
    }

    value(value) {
        if (value === undefined) {
            return this.getValue();
        }
        this.inputElement.value(value);
        return this;
    }

    // Re-convert and relabel the field when the metric/imperial toggle flips.
    //
    // Element and segment dimension inputs are re-populated from the shape's
    // pixel size on this same event (ElementInput / MultiField re-run their
    // populate), so their displayed value reconverts automatically. Draw-mode
    // inputs have no backing shape to repopulate from, so the number they show
    // would otherwise just be relabelled — leaving "1 m" reading as "1 ft"
    // instead of the correct ~3.28 ft. Reconvert it in place here, mirroring
    // how DrawMenu reconverts the stroke-width field.
    updateUnitSystem(isMetric) {
        if (this.isDrawInput) {
            const current = parseFloat(String(this.inputElement.value()).replace(',', '.'));
            if (!isNaN(current) && current !== 0) {
                // The field still holds the previous unit system's value, so
                // convert it back to pixels using the outgoing units (the
                // opposite of the incoming isMetric) before re-displaying.
                const pixels = current * (isMetric ? 30.48 : 100);
                this.inputElement.value(this.calculatePixelToDisplayDistance(pixels, isMetric));
            }
        }
        this.unit.html(isMetric ? 'm' : 'ft');
    }

    makeSticky() {
        if (this.getValue() <= 0) {
            return;
        }
        this.tools.currentTool.makeSticky(this);
        this.inputElement.style('border', '2px solid green');
    }

    focus() {
        this.inputElement.elt.focus();
    }

    getValue() {
        return this.parse(this.inputElement.value());
    }

    setFocusAndBlurActions() {
        this.inputElement.elt.onfocus = () => {
            this.enableHotKeys = false;
            this.getTab('info').activeInput = this.inputElement;
            if (!this.isDrawInput) {
                this.action = this.actions.new(
                    FIELD_INPUT,
                    this.getTab('info').currentElement,
                    this,
                    this.inputFunction
                );
            }
        };
        this.inputElement.elt.onblur = () => {
            this.enableHotKeys = true;
            this.action?.finish();
            let activeInput = this.getTab('info').activeInput;
            this.getTab('info').activeInput = activeInput == this.inputElement
                ? null
                : activeInput;
        };
    }

    parse(value) {
        if (!this.dimensionInput) {
            return value;
        }
        value = parseFloat(value.replace(',', '.')) || 0;
        value = this.settings.metric ? value : value * 0.3048;
        return value * 100;
    }

    reset() {
        this.inputElement.value('');
    }
}
